<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';

const props = defineProps({
  items: { type: Array, default: () => [] },
  installments: { type: Array, default: () => [] },
  observations: { type: String, default: '' },
});

const { t } = useI18n();
const empty = () => t('SALES.DETAIL.EMPTY_VALUE');
const summaryLabels = computed(() => ({
  total: t('SALES.DELIVERY.SUMMARY.COMMISSION.TOTAL'),
  deduction: t('SALES.DELIVERY.SUMMARY.COMMISSION.DEDUCTION'),
  net: t('SALES.DELIVERY.SUMMARY.COMMISSION.NET'),
  installments: t('SALES.DELIVERY.SUMMARY.COMMISSION.INSTALLMENT_COUNT'),
}));
const fact = key => {
  const item = props.items.find(current => current.key === key) || {
    key,
    label: '',
    value: empty(),
  };
  return { ...item, label: summaryLabels.value[key] || item.label };
};
const value = current => current || empty();
const money = current => current?.amount || value(current);
const generalRows = computed(() => [
  [fact('type')],
  [fact('total'), fact('deduction'), fact('net')],
  [fact('payment'), fact('installments')],
]);
const installmentRows = computed(() =>
  props.installments.map((installment, index) => [
    {
      key: `installment-${installment.id || index}`,
      label: t('SALES.DETAIL.COMMISSION.INSTALLMENT_ORDINAL', {
        number: installment.numero_parcela || index + 1,
      }),
      value: money(installment.valor_parcela),
    },
    {
      key: `payment-period-${installment.id || index}`,
      label: t('SALES.DELIVERY.SUMMARY.COMMISSION.PAYMENT_PERIOD'),
      value: value(installment.periodo_pagamento || installment.nome_periodo),
    },
  ])
);
const observations = computed(() => ({
  key: 'observations',
  label: t('SALES.DETAIL.OBSERVATIONS'),
  value: value(props.observations),
}));
</script>

<template>
  <LaisCard :title="t('SALES.DETAIL.COMMISSION.SUMMARY_GENERAL_TITLE')">
    <div class="grid gap-6">
      <dl
        v-for="(row, index) in generalRows"
        :key="`general-${index}`"
        class="grid gap-6 sm:grid-cols-[repeat(auto-fit,minmax(140px,1fr))]"
      >
        <div v-for="item in row" :key="item.key" class="min-w-0">
          <dt class="text-base font-bold leading-[22px] text-lais-text">
            {{ item.label }}
          </dt>
          <dd
            class="mt-1 min-w-[110px] break-words text-base leading-[22px] text-lais-text-muted"
          >
            {{ item.value }}
          </dd>
        </div>
      </dl>
      <dl
        v-for="(row, index) in installmentRows"
        :key="`installment-${index}`"
        class="grid gap-6 sm:grid-cols-[repeat(auto-fit,minmax(140px,1fr))]"
      >
        <div v-for="item in row" :key="item.key" class="min-w-0">
          <dt class="text-base font-bold leading-[22px] text-lais-text">
            {{ item.label }}
          </dt>
          <dd
            class="mt-1 min-w-[110px] break-words text-base leading-[22px] text-lais-text-muted"
          >
            {{ item.value }}
          </dd>
        </div>
      </dl>
      <dl>
        <div class="min-w-0">
          <dt class="text-base font-bold leading-[22px] text-lais-text">
            {{ observations.label }}
          </dt>
          <dd
            class="mt-1 min-w-[110px] whitespace-pre-wrap break-words text-base leading-[22px] text-lais-text-muted"
          >
            {{ observations.value }}
          </dd>
        </div>
      </dl>
    </div>
  </LaisCard>
</template>
